import type { Messages } from '@lingui/core'
import { useLingui } from '@lingui/react/macro'

/**
 * Keyed by the ACTIVE CATALOG alone, never by the `I18n` instance holding it. The strings depend only on
 * the catalog, and the catalog object is module-level (`loadTranslation` memoizes it per locale), so one
 * build serves every render in the process. Keying by the instance as well missed on every SSR request —
 * the server gives each request its own instance (see `createLocaleI18n`), so the ~220 lookups below ran
 * again per document while the client, on one shared instance, never noticed.
 */
const listsCache = new WeakMap<Messages, object>()

function cachedLists<T extends object>(messages: Messages, build: () => T): T {
  let lists = listsCache.get(messages) as T | undefined
  if (!lists) listsCache.set(messages, (lists = build()))
  return lists
}

export function useLists() {
  const { i18n, t } = useLingui()

  return cachedLists(i18n.messages, () => ({
    educationalLevel: [
      t`No studies`,
      t`Primary school`,
      t`High school`,
      t`Vocational training`,
      t`Undergraduate degree`,
      t`Master's degree`,
      t`Doctorate`,
    ],
    level: {
      neutral: [
        t({ context: 'neutral', message: 'Intern' }),
        t({ context: 'neutral', message: 'Employee' }),
        t({ context: 'neutral', message: 'Specialist' }),
        t({ context: 'neutral', message: 'Middle management' }),
        t({ context: 'neutral', message: 'Manager' }),
        t({ context: 'neutral', message: 'Director' }),
      ],
      male: [
        t({ context: 'male', message: 'Intern' }),
        t({ context: 'male', message: 'Employee' }),
        t({ context: 'male', message: 'Specialist' }),
        t({ context: 'male', message: 'Middle management' }),
        t({ context: 'male', message: 'Manager' }),
        t({ context: 'male', message: 'Director' }),
      ],
      female: [
        t({ context: 'female', message: 'Intern' }),
        t({ context: 'female', message: 'Employee' }),
        t({ context: 'female', message: 'Specialist' }),
        t({ context: 'female', message: 'Middle management' }),
        t({ context: 'female', message: 'Manager' }),
        t({ context: 'female', message: 'Director' }),
      ],
    },
    function: [
      t`Administration`,
      t`Analysis`,
      t`Art/Creativity`,
      t`Customer Support`,
      t`Medical care`,
      t`Supply chain`,
      t`Training`,
      t`Science`,
      t`Purchasing`,
      t`Consultancy`,
      t`Accounting/Audits`,
      t`QA`,
      t`Business development`,
      t`Design`,
      t`Distribution`,
      t`Education`,
      t`Strategy / Planning`,
      t`Finance`,
      t`Management`,
      t`Product management`,
      t`Project management`,
      t`Engineering`,
      t`Investigation`,
      t`Legal`,
      t`Manufacture`,
      t`Marketing`,
      t`Business`,
      t`Production`,
      t`Advertising`,
      t`Human Resources`,
      t`Writing and reviewing`,
      t`Public relations`,
      t`Information technology`,
      t`Sales`,
      t`Function not defined`,
    ],
    sector: [
      t`Government administration`,
      t`Aeronautics / Aviation`,
      t`Farming`,
      t`Alimentation and drinks`,
      t`Storage`,
      t`Animation`,
      t`Betting and casinos`,
      t`Architecture and planning`,
      t`Performing arts`,
      t`Crafts`,
      t`Consumer goods`,
      t`Luxury items and jewelry`,
      t`Sports articles`,
      t`International affairs`,
      t`Mental health care`,
      t`Health and hospital care`,
      t`Automotive`,
      t`Industrial automation`,
      t`Banking`,
      t`Investment banking`,
      t`Fine arts`,
      t`Libraries`,
      t`Real estate`,
      t`Commercial real estate`,
      t`Biotechnology`,
      t`Venture capital and private capital`,
      t`Construction`,
      t`Naval building`,
      t`Strategy and operations consulting`,
      t`Accounting`,
      t`Cosmetics`,
      t`Glass, ceramics and concrete`,
      t`Compliance with the law`,
      t`Department of defense and outer space`,
      t`Sports`,
      t`Law`,
      t`Programming development`,
      t`Development and international trade`,
      t`Design`,
      t`Graphic design`,
      t`Staffing and selection of personnel`,
      t`Primary / secondary education`,
      t`Army`,
      t`E-learning`,
      t`Consumer electronics`,
      t`Container packing`,
      t`Renewable energy and environment`,
      t`Higher education`,
      t`Entertainment`,
      t`Package and freight shipping`,
      t`Computer equipment`,
      t`Writing and editing`,
      t`Philanthropy`,
      t`Vocational training and training`,
      t`Photography`,
      t`Strategic cabinets`,
      t`Cattle raising`,
      t`Investment Management`,
      t`Management of non-profit organizations`,
      t`Education Management`,
      t`Hostelry`,
      t`Import and export`,
      t`Printing`,
      t`Aerospace and aviation industry`,
      t`Pharmaceutical industry`,
      t`Textile and fashion industry`,
      t`Civil Engineering`,
      t`Industrial or mechanical engineering`,
      t`Recreational facilities and services`,
      t`Religious institutions`,
      t`Network interconnection`,
      t`Internet`,
      t`Investigation`,
      t`Market research`,
      t`Judicial`,
      t`Dairy products`,
      t`Logistics and supply chain`,
      t`Electrical/electronic manufacturing`,
      t`Railway manufacturing`,
      t`Machinery`,
      t`Marketing and publicity`,
      t`Business equipment and supplies`,
      t`Construction materials`,
      t`Alternative medicine`,
      t`Online media`,
      t`Broadcast media`,
      t`Capital markets`,
      t`Mining and metallurgy`,
      t`Furniture`,
      t`Museums and institutions`,
      t`Music`,
      t`Nanotechnology`,
      t`Naval`,
      t`Leisure, travel and tourism`,
      t`Executive office`,
      t`Legislative office`,
      t`Civic and social organization`,
      t`Political organization`,
      t`Film and cinema`,
      t`Newspapers`,
      t`Oil and energy`,
      t`Pisciculture`,
      t`Plastics`,
      t`Public politics`,
      t`Food production`,
      t`Multimedia production`,
      t`Paper and forest products`,
      t`Chemical products`,
      t`Medical professions`,
      t`Civil protection`,
      t`Publications`,
      t`Fundraising`,
      t`Human Resources`,
      t`Government relations`,
      t`Public relations and communications`,
      t`Resolution of conflicts by third parties`,
      t`Restaurants`,
      t`Health, well-being and exercise`,
      t`Computer and networks security`,
      t`Security and investigations`,
      t`Insurance`,
      t`Semiconductors`,
      t`Customer service`,
      t`Event service`,
      t`Information service`,
      t`Financial services`,
      t`Infrastructure services`,
      t`Juridical services`,
      t`Medical services`,
      t`Environmental services`,
      t`Services for the individual and the family`,
      t`Public services`,
      t`Services and information technologies`,
      t`Software`,
      t`Subcontrataciones / Offshoring`,
      t`Supermarkets`,
      t`Tobacco`,
      t`Wireless technology`,
      t`Telecommunications`,
      t`Textile`,
      t`Translation and localization`,
      t`Road or rail transport`,
      t`Wholesale`,
      t`Retail`,
      t`Veterinary`,
      t`Video game`,
      t`Wines and liquors`,
      t`Sector not specified`,
    ],
    employees: [
      t`One employee`,
      t`2 to 10 employees`,
      t`11 to 50 employees`,
      t`51 to 200 employees`,
      t`201 to 500 employees`,
      t`501 to 1,000 employees`,
      t`1,001 to 5,000 employees`,
      t`5,001 to 10,000 employees`,
      t`More than 10,000 employees`,
    ],
  }))
}
